import React from "react";
import { Network, Microscope, Coins, HeartHandshake } from "lucide-react";

const REASONS = [
    {
        Icon: Network,
        title: "Deep Rural Network",
        body: "Direct community partnerships ensuring authentic, grounded innovation — not parachuted-in solutions.",
    },
    {
        Icon: Microscope,
        title: "Scientific Rigor",
        body: "PhD-level biotech expertise combined with practical, shop-floor and field-tested application.",
    },
    {
        Icon: Coins,
        title: "Dual Revenue Streams",
        body: "B2C rural products plus B2B industrial consulting and product development — resilient by design.",
    },
    {
        Icon: HeartHandshake,
        title: "Impact-First Model",
        body: "Measurable social returns create brand loyalty and sharp market differentiation.",
    },
];

export const WhyChooseUs = () => {
    return (
        <section
            id="why"
            data-testid="why-section"
            className="section-pad relative overflow-hidden bg-card/40"
        >
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-copper))]/15"
                style={{ width: 520, height: 520, top: -140, right: -120 }}
            />
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-moss))]/15"
                style={{ width: 520, height: 520, bottom: -180, left: -160 }}
            />

            <div className="container-x relative">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
                    <div className="lg:col-span-7">
                        <p className="overline">
                            <span className="eyebrow-line" />
                            Why choose us?
                        </p>
                        <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.02]">
                            Four reasons to{" "}
                            <em className="not-italic text-primary">
                                partner with Yaalee
                            </em>
                            .
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-base md:text-lg leading-relaxed text-muted-foreground">
                        Purpose-built as a bridge between rigorous science, rural
                        community, and industrial reality — our edge is the combination,
                        not any single capability.
                    </p>
                </div>

                {/* Alternating big numbered reason cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {REASONS.map(({ Icon, title, body }, i) => (
                        <article
                            key={title}
                            data-testid={`why-card-${i}`}
                            className="group relative rounded-[1.75rem] border border-border bg-background/70 backdrop-blur-sm p-8 md:p-10 overflow-hidden transition-all duration-500 hover:border-primary/60 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/5"
                        >
                            {/* Oversized index watermark */}
                            <span
                                aria-hidden
                                className="pointer-events-none select-none absolute -bottom-6 -right-2 font-display text-[12rem] leading-none font-medium text-foreground/[0.06] group-hover:text-primary/10 transition-colors duration-500"
                            >
                                0{i + 1}
                            </span>

                            {/* Corner glow */}
                            <div
                                aria-hidden
                                className="pointer-events-none absolute -top-24 -right-24 h-52 w-52 rounded-full bg-primary/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                            />

                            <div className="relative flex items-start gap-5">
                                <div className="shrink-0 h-14 w-14 rounded-2xl bg-primary/15 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                                    <Icon className="h-6 w-6" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-[11px] uppercase tracking-[0.22em] text-primary font-semibold">
                                        Reason 0{i + 1}
                                    </p>
                                    <h3 className="mt-2 font-display text-[28px] md:text-[32px] font-medium tracking-tight leading-tight">
                                        {title}
                                    </h3>
                                    <div className="mt-5 h-px w-12 bg-border group-hover:w-20 group-hover:bg-primary transition-all duration-500" />
                                    <p className="mt-5 text-[15px] leading-relaxed text-muted-foreground max-w-md">
                                        {body}
                                    </p>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                {/* Quiet footer caption */}
                <div className="mt-14 flex items-center justify-center">
                    <span className="inline-block text-[11px] uppercase tracking-[0.22em] text-muted-foreground divider-leaf">
                        Science · Community · Commerce · Impact
                    </span>
                </div>
            </div>
        </section>
    );
};

export default WhyChooseUs;
